// 共用 treemap 工具: 深色调色板 + 明暗派生 + buildTreemapData(含 Other 顶层合并)
// + tooltip formatter + 完整 treemap series 配置工厂。参数化 fieldName, 多页共用。
//
// 重要(防回归): 节点颜色【直接写在 itemStyle.color】(顶层从调色板循环, 子层 deriveChildColor 向暗)。
//   不要在 series.levels 里配 color / colorSaturation —— 本项目 echarts5 版本下 level 调色板
//   不生效, 顶层块会变成无填充的白块(已踩过坑)。颜色全靠节点自带 itemStyle.color。

// 顶层分类配色(蓝青紫系深色, 对白字对比度足够)
export const TREEMAP_COLORS = [
  '#2c5f8a', '#1f7a5c', '#5b4b8a', '#2d7d9a', '#7d4f7a',
  '#a85d2e', '#3a7d44', '#44548c', '#8a4a42', '#4a6670',
]

// Other 块固定中性灰(与彩色父块区分; 对白字对比度足够)
export const OTHER_COLOR = '#6b7280'

// Other 顶层合并参数(可调)
export const OTHER_THRESHOLD = 0.03 // 占比 < 3% 视为"小块"
export const OTHER_MIN_COUNT = 2 // 至少 2 个小块才合并成 Other(只有1个不值得)

// 在父色相基础上做明暗: ratio>0 变亮(向白), ratio<0 变暗(向黑)
export function shadeColor(hex, ratio) {
  let h = String(hex || '').replace('#', '')
  h = h.length === 3 ? h.split('').map(c => c + c).join('') : h.padEnd(6, '0')
  const r = parseInt(h.slice(0, 2), 16) || 0
  const g = parseInt(h.slice(2, 4), 16) || 0
  const b = parseInt(h.slice(4, 6), 16) || 0
  const f = v => {
    const x = ratio >= 0 ? v + (255 - v) * ratio : v * (1 + ratio)
    return Math.round(Math.min(255, Math.max(0, x))).toString(16).padStart(2, '0')
  }
  return '#' + f(r) + f(g) + f(b)
}

// 子节点继承父色相, 在父色基础上逐档变暗(向黑), 保证白字对比度
export function deriveChildColor(parentColor, index, count) {
  const step = count > 1 ? 0.3 / (count - 1) : 0
  return shadeColor(parentColor || TREEMAP_COLORS[0], -(0.05 + step * index))
}

// 树形 records 递归转 echarts treemap data。
//  - fieldName: 当前页节点显示名字段(wrapUp/browser/device/fromPage/location)
//  - depth 0 从调色板循环取色(Other 走灰色); 子层 deriveChildColor 继承父色相做明暗
//  - 直接写 itemStyle.color, 不依赖 level 调色板
export function buildTreemapData(nodes, fieldName, depth = 0, parentColor = null) {
  if (!Array.isArray(nodes) || nodes.length === 0) return []
  const count = nodes.length
  return nodes.map((item, index) => {
    const color = depth === 0
      ? (item.isOther ? OTHER_COLOR : TREEMAP_COLORS[index % TREEMAP_COLORS.length])
      : deriveChildColor(parentColor, index, count)
    const node = {
      name: item[fieldName] != null ? String(item[fieldName]) : '',
      value: Number(item.count) || 0,
      percentage: item.percentage, // Other 节点为 undefined, formatter 走现算分支
      itemStyle: { color },
    }
    if (Array.isArray(item.children) && item.children.length > 0) {
      node.children = buildTreemapData(item.children, fieldName, depth + 1, color)
    }
    return node
  })
}

// 顶层 Other 合并: 把占比 < threshold 的小父类(至少 minCount 个)合并成一个 Other 节点。
//  - Other.value = 小块 count 之和; Other.children = 那些小块原始节点(保留各自 children, 可下钻看明细)
//  - 只对传入的顶层数组操作, 不递归(下钻后的子类完整保留)
//  - 返回新数组(大块原序 + 末尾追加 Other), 不改原 records(下游 table/download/totalCount 不受影响)
export function mergeTopLevelOther(records, fieldName, opts = {}) {
  const threshold = opts.threshold != null ? opts.threshold : OTHER_THRESHOLD
  const minCount = opts.minCount != null ? opts.minCount : OTHER_MIN_COUNT
  if (!Array.isArray(records) || records.length === 0) return []
  const total = records.reduce((acc, it) => acc + (Number(it.count) || 0), 0)
  if (total <= 0) return records.slice()

  const big = []
  const small = []
  records.forEach(it => {
    const ratio = (Number(it.count) || 0) / total
    if (ratio < threshold) small.push(it)
    else big.push(it)
  })

  // 小块不足 minCount: 不做 Other, 原样返回
  if (small.length < minCount) return records.slice()

  const otherValue = small.reduce((acc, it) => acc + (Number(it.count) || 0), 0)
  const otherNode = {
    isOther: true,
    [fieldName]: 'Other',
    count: otherValue,
    children: small, // 保留小块原始节点(含各自 children), 下钻 Other 看明细
  }
  return [...big, otherNode]
}

// tooltip formatter 工厂: 闭包持有 getTotalCount(返回当前页 totalCount)。
//  - 优先用节点 percentage 字段; 缺失(Other / 后端没给)用 value/totalCount 现算
//  - HTML 转义节点名
function makeTooltipFormatter(getTotalCount) {
  return function (info) {
    const data = info.data || {}
    const name = data.name != null ? data.name : info.name
    const value = data.value != null ? data.value : info.value
    let pctText = ''
    if (data.percentage != null && data.percentage !== '') {
      pctText = `${data.percentage}%`
    } else {
      const tc = typeof getTotalCount === 'function' ? Number(getTotalCount()) : 0
      if (tc > 0) pctText = `${((Number(value) || 0) / tc * 100).toFixed(2)}%`
    }
    const safeName = String(name == null ? '' : name)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    return [
      `<div style="font-weight:600;margin-bottom:4px;max-width:260px;white-space:normal;word-break:break-word;">${safeName}</div>`,
      `<div>Count : ${value}</div>`,
      pctText ? `<div>Percentage : ${pctText}</div>` : '',
    ].join('')
  }
}

// 主工厂: 传入 records/fieldName/getTotalCount, 返回 { tooltip, series } 直接塞进页面 options。
//  - 内部先 mergeTopLevelOther(顶层 Other), 再 buildTreemapData
//  - other:false 时跳过合并
export function buildTreemapOption({ records, fieldName, getTotalCount, other = true, otherOpts }) {
  const topLevel = other ? mergeTopLevelOther(records, fieldName, otherOpts || {}) : (records || [])
  return {
    tooltip: {
      trigger: 'item',
      formatter: makeTooltipFormatter(getTotalCount),
    },
    series: [
      {
        type: 'treemap',
        top: 30,
        left: 0,
        right: 0,
        bottom: 0,
        roam: false,
        nodeClick: 'zoomToNode',
        leafDepth: 1, // 默认只显示顶层父类, 点击下钻
        upperLabel: {
          show: true,
          height: 20,
          color: '#fff',
          fontSize: 12,
          overflow: 'truncate',
          textBorderColor: 'rgba(0,0,0,0.3)',
          textBorderWidth: 2,
        },
        label: {
          show: true,
          color: '#fff',
          fontSize: 12,
          overflow: 'truncate',
          textBorderColor: 'rgba(0,0,0,0.3)',
          textBorderWidth: 2,
          formatter(p) {
            const v = p.value != null ? p.value : ''
            return `${p.name}\n${v}`
          },
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
          gapWidth: 1,
        },
        breadcrumb: {
          show: true,
          top: 0,
          left: 'center',
          height: 22,
          emptyItemWidth: 25,
          itemStyle: {
            color: '#f0f2f5',
            borderColor: '#dcdfe6',
            borderWidth: 1,
            textStyle: { color: '#606266', fontSize: 12 },
          },
          emphasis: {
            itemStyle: { color: '#e6f0ff' },
          },
        },
        levels: [
          {
            itemStyle: { borderColor: '#fff', borderWidth: 0, gapWidth: 1 },
            upperLabel: { show: false },
          },
          {
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 3,
              gapWidth: 3,
              borderColorSaturation: 0.6,
            },
            upperLabel: { show: true },
          },
          {
            itemStyle: {
              borderColorSaturation: 0.6,
              gapWidth: 1,
              borderWidth: 1,
            },
          },
        ],
        data: buildTreemapData(topLevel, fieldName),
      },
    ],
  }
}
